import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Alert, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaFileExcel, FaArrowLeft, FaUser, FaEye, FaDownload } from "react-icons/fa";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { reporteApi } from "../../services/api/reporteApi";
import "react-toastify/dist/ReactToastify.css";

const ConsultaAtencionesComensales = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [comensalesEncontrados, setComensalesEncontrados] = useState([]);
  const [comensalSeleccionado, setComensalSeleccionado] = useState(null);
  const [atenciones, setAtenciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Estados para paginación
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  const handleBuscarComensal = async (e) => {
    e.preventDefault();

    if (!numeroDocumento.trim()) {
      toast.error("Por favor ingrese un número de documento");
      return;
    }

    setLoading(true);
    setSearchPerformed(true);
    setComensalesEncontrados([]);
    setComensalSeleccionado(null);
    setAtenciones([]);

    try {
      const response = await reporteApi.buscarComensalParaConsulta(numeroDocumento.trim());

      if (response.data.success) {
        setComensalesEncontrados(response.data.comensales);
        if (response.data.comensales.length === 0) {
          toast.info("No se encontraron comensales con ese número de documento");
        } else {
          toast.success(`Se encontraron ${response.data.comensales.length} comensales`);
        }
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      console.error("Error al buscar comensal:", error);
      toast.error(`Error al buscar el comensal: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSeleccionarComensal = async (comensal, page = 1) => {
    setComensalSeleccionado(comensal);
    setLoading(true);

    console.log("🔍 Seleccionando comensal:", comensal);
    console.log("🔍 Parámetros para getAtencionesReporte:", {
      comensalId: comensal.id,
      clienteId: comensal.id_cliente,
      page: page
    });

    try {
      const response = await reporteApi.obtenerAtencionesComensal(comensal.id, page);

      console.log("🔍 Respuesta de atenciones:", response);

      if (response.data.success) {
        console.log("🔍 Atenciones recibidas:", response.data.atenciones);
        console.log("🔍 Paginación recibida:", response.data.pagination);
        
        setAtenciones(response.data.atenciones);
        setPagination(response.data.pagination);
        
        if (response.data.atenciones.length === 0) {
          toast.info("No se encontraron atenciones para este comensal en el cliente seleccionado");
        } else {
          toast.success(`Se encontraron ${response.data.pagination.total} atenciones (página ${response.data.pagination.current_page} de ${response.data.pagination.last_page})`);
        }
      } else {
        console.log("🔍 Error en respuesta:", response.data.message);
        toast.warning(response.data.message);
        setAtenciones([]);
        setPagination({
          current_page: 1,
          per_page: 10,
          total: 0,
          last_page: 1,
          from: 0,
          to: 0,
          has_more_pages: false
        });
      }
    } catch (error) {
      console.error("🔍 Error al obtener atenciones:", error);
      toast.error("Error al obtener las atenciones del comensal");
      setAtenciones([]);
      setPagination({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
        has_more_pages: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    if (!comensalSeleccionado) {
      toast.warning("No hay comensal seleccionado para exportar");
      return;
    }

    setLoading(true);
    
    try {
      toast.info("Generando archivo Excel con formato profesional...");
      
      const response = await reporteApi.descargarExcelAtencionesComensal(comensalSeleccionado.id);
      
      // Crear URL para descargar el archivo
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generar nombre del archivo
      const fileName = `consulta_atenciones_${comensalSeleccionado.numero_documento_identidad}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Archivo Excel descargado exitosamente con formato mejorado");
    } catch (error) {
      console.error("Error descargando Excel:", error);
      const errorMessage = error.response?.data?.message || "Error al generar el archivo Excel";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    if (typeof dateString === 'string') {
      const datePart = dateString.split(/[T ]/)[0];
      const parts = datePart.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      {/* Estilos Digital Harmony (reutilizando los del reporte anterior) */}
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }

          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }

          .dh-main-title {
            font-size: 2.5rem;
            font-weight: 700;
            margin: 0;
          }

          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }

          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }

          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            font-weight: 700;
          }

          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }

          .dh-card-header-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
          }

          .dh-card-header-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            color: white;
          }

          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }

          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-info:hover {
            background: linear-gradient(135deg, #0072ff 0%, #0056d1 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }

          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }

          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }

          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }

          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }

          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 8px;
          }

          .dh-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          }

          .dh-table thead th {
            background: #374151;
            color: white;
            font-weight: 700;
            font-size: 0.875rem;
            text-transform: uppercase;
            padding: 1rem 0.75rem;
            border: none;
          }

          .dh-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
          }

          .dh-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%);
          }

          .dh-badge-success {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }

          .dh-badge-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
          }

          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .dh-alert-info {
            background: linear-gradient(135deg, rgba(0, 198, 255, 0.1) 0%, rgba(0, 114, 255, 0.1) 100%);
            border-left: 4px solid #00c6ff;
            color: #0056d1;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />

        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaUser className="me-3" />
                  Consulta de Atenciones por Comensal
                </h1>
                <p className="dh-main-subtitle">Búsqueda de servicios por número de documento</p>
              </div>
              <Button
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          {/* Sección de búsqueda */}
          <Row className="mb-4">
            <Col lg={8}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <h5 className="mb-0">
                    <FaSearch className="me-2" />
                    Búsqueda de Comensal
                  </h5>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <Form onSubmit={handleBuscarComensal}>
                    <Row className="align-items-end">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="dh-form-label">Número de Documento de Identidad</Form.Label>
                          <Form.Control
                            type="text"
                            value={numeroDocumento}
                            onChange={(e) => setNumeroDocumento(e.target.value)}
                            placeholder="Ingrese el número de documento de identidad"
                            className="dh-form-control"
                            maxLength={20}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Button
                          type="submit"
                          disabled={loading || !numeroDocumento.trim()}
                          className="btn-dh-primary w-100 d-flex align-items-center justify-content-center"
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Buscando...
                            </>
                          ) : (
                            <>
                              <FaSearch className="me-2" />
                              Buscar
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-info">
                  <h6 className="mb-0">
                    <FaEye className="me-2" />
                    Instrucciones
                  </h6>
                </Card.Header>
                <Card.Body style={{ padding: '1.5rem' }}>
                  <div className="dh-alert dh-alert-info">
                    <strong>Búsqueda por documento:</strong>
                    <p className="mb-2 mt-2">
                      Ingrese el número de documento de identidad del comensal para buscar todos sus registros en el sistema.
                    </p>
                    <small className="text-muted">
                      Si el comensal está en múltiples empresas, aparecerán todas las opciones para seleccionar.
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Resultados de búsqueda de comensales */}
          {searchPerformed && comensalesEncontrados.length > 0 && (
            <Row className="mb-4">
              <Col>
                <Card className="dh-card">
                  <Card.Header className="dh-card-header dh-card-header-success">
                    <h5 className="mb-0">
                      <FaUser className="me-2" />
                      Comensales Encontrados ({comensalesEncontrados.length})
                    </h5>
                  </Card.Header>
                  <Card.Body style={{ padding: '1.5rem' }}>
                    <Table className="dh-table" responsive>
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Tipo Documento</th>
                          <th>N° Documento</th>
                          <th>Cliente</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comensalesEncontrados.map((comensal) => (
                          <tr key={`${comensal.id}-${comensal.id_cliente}`}>
                            <td><strong>{comensal.nombre}</strong></td>
                            <td>{comensal.tipo_documento}</td>
                            <td>
                              <Badge className="dh-badge-info">{comensal.numero_documento_identidad}</Badge>
                            </td>
                            <td>{comensal.nombre_cliente}</td>
                            <td>
                              <Button
                                size="sm"
                                className="btn-dh-primary"
                                onClick={() => handleSeleccionarComensal(comensal)}
                                disabled={loading}
                              >
                                <FaEye className="me-1" />
                                Ver Atenciones
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Información del comensal seleccionado y sus atenciones */}
          {comensalSeleccionado && (
            <Row>
              <Col>
                <Card className="dh-card">
                  <Card.Header className="dh-card-header dh-card-header-info d-flex justify-content-between align-items-center">
                    <div>
                      <h5 className="mb-1">
                        <FaUser className="me-2" />
                        Atenciones de {comensalSeleccionado.nombre}
                      </h5>
                      <small className="opacity-75">
                        {comensalSeleccionado.tipo_documento}: {comensalSeleccionado.numero_documento_identidad} |
                        Cliente: {comensalSeleccionado.nombre_cliente}
                      </small>
                    </div>
                    {atenciones.length > 0 && (
                      <Button
                        className="btn-dh-primary d-flex align-items-center"
                        onClick={handleExportarExcel}
                      >
                        <FaFileExcel className="me-2" />
                        Exportar Excel
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body style={{ padding: '1.5rem' }}>
                    {loading ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Cargando atenciones...</span>
                        </div>
                        <p className="mt-2 text-muted">Cargando atenciones...</p>
                      </div>
                    ) : atenciones.length > 0 ? (
                      <>
                        <div className="mb-3">
                          <Badge className="dh-badge-success">
                            Total de atenciones: {pagination.total} | Mostrando: {pagination.from}-{pagination.to}
                          </Badge>
                        </div>
                        <Table className="dh-table" responsive>
                          <thead>
                            <tr>
                              <th>Fecha de Servicio</th>
                              <th>Tipo de Servicio</th>
                              <th>Comedor</th>
                              <th>Fecha de Registro</th>
                            </tr>
                          </thead>
                          <tbody>
                            {atenciones.map((atencion, index) => (
                              <tr key={atencion.id || index}>
                                <td>
                                  <Badge className="dh-badge-info">
                                    {formatDate(atencion.fecha_servicio)}
                                  </Badge>
                                </td>
                                <td><strong>{atencion.tipo_servicio}</strong></td>
                                <td>{atencion.comedor}</td>
                                <td className="text-muted small">
                                  {formatDateTime(atencion.fecha_registro)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        
                        {/* Controles de paginación */}
                        {pagination.last_page > 1 && (
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="d-flex align-items-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={pagination.current_page === 1 || loading}
                                onClick={() => handleSeleccionarComensal(comensalSeleccionado, 1)}
                                className="me-2"
                              >
                                Primera
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={pagination.current_page === 1 || loading}
                                onClick={() => handleSeleccionarComensal(comensalSeleccionado, pagination.current_page - 1)}
                                className="me-2"
                              >
                                Anterior
                              </Button>
                            </div>
                            
                            <div className="d-flex align-items-center">
                              <span className="text-muted small me-3">
                                Página {pagination.current_page} de {pagination.last_page}
                              </span>
                            </div>
                            
                            <div className="d-flex align-items-center">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={!pagination.has_more_pages || loading}
                                onClick={() => handleSeleccionarComensal(comensalSeleccionado, pagination.current_page + 1)}
                                className="me-2"
                              >
                                Siguiente
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                disabled={pagination.current_page === pagination.last_page || loading}
                                onClick={() => handleSeleccionarComensal(comensalSeleccionado, pagination.last_page)}
                              >
                                Última
                              </Button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <Alert variant="info" className="dh-alert dh-alert-info">
                        <strong>Sin atenciones</strong><br />
                        No se encontraron atenciones para este comensal en el cliente seleccionado.
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Mensaje cuando no se encuentra ningún comensal */}
          {searchPerformed && comensalesEncontrados.length === 0 && !loading && (
            <Row>
              <Col>
                <Alert variant="warning" className="dh-alert">
                  <strong>Sin resultados</strong><br />
                  No se encontraron comensales con el número de documento ingresado.
                </Alert>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </>
  );
};

export default ConsultaAtencionesComensales;